import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';

import { SnackbarKey, useSnackbar } from 'notistack';

import { ReactComponent as SnackbarErrorIcon } from '@/assets/icons/snackbar-error.svg';
import { ReactComponent as SnackbarInfoIcon } from '@/assets/icons/snackbar-info.svg';
import { ReactComponent as SnackbarSuccessIcon } from '@/assets/icons/snackbar-success.svg';
import { ReactComponent as SnackbarWarningIcon } from '@/assets/icons/snackbar-warning.svg';

export type SnackbarVariant = 'success' | 'error' | 'warning' | 'info' | 'default';

interface CustomSnackbarContentProps {
  id: SnackbarKey;
  message: string | React.ReactNode;
  variant: SnackbarVariant;
  autoHideDuration?: number | null;
}

const VARIANT_ICONS: Partial<Record<SnackbarVariant, React.FC<React.SVGProps<SVGSVGElement>>>> = {
  success: SnackbarSuccessIcon,
  error: SnackbarErrorIcon,
  warning: SnackbarWarningIcon,
  info: SnackbarInfoIcon,
};

const VARIANT_TITLE_KEYS: Record<SnackbarVariant, string> = {
  success: 'snackbar_success_title',
  error: 'snackbar_error_title',
  warning: 'snackbar_warning_title',
  info: 'snackbar_info_title',
  default: 'snackbar_info_title',
};

const CIRCLE_RADIUS = 9;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const countdown = keyframes`
  from { stroke-dashoffset: ${CIRCLE_CIRCUMFERENCE}; }
  to { stroke-dashoffset: 0; }
`;

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  width: '21.5rem',
  padding: '0.5rem',
  borderRadius: '0.5rem',
  backgroundColor: '#FEFEFE',
  boxShadow: '0 0.0625rem 0.5rem 0 rgba(0, 0, 0, 0.15)',
  fontFamily: theme.typography.fontFamily,
  '&:hover .countdown-arc': {
    animationPlayState: 'paused',
  },
}));

interface CountdownCloseButtonProps {
  duration: number | null;
  onClick: () => void;
}

const CountdownCloseButton = ({ duration, onClick }: CountdownCloseButtonProps) => (
  <Box
    onClick={onClick}
    sx={{
      flexShrink: 0,
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
      position: 'relative',
      '&:hover': { opacity: 0.7 },
      '& .countdown-arc': {
        strokeDashoffset: CIRCLE_CIRCUMFERENCE,
        ...(duration ? { animation: `${countdown} ${duration}ms linear forwards` } : {}),
      },
    }}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r={CIRCLE_RADIUS} stroke="#DBDBE2" strokeWidth="1.5" fill="none" />
      {duration && (
        <circle
          className="countdown-arc"
          cx="10"
          cy="10"
          r={CIRCLE_RADIUS}
          stroke="#8F8EA3"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={CIRCLE_CIRCUMFERENCE}
          transform="rotate(-90 10 10)"
        />
      )}
      <path
        d="M7.5 7.5L12.5 12.5M12.5 7.5L7.5 12.5"
        stroke="#8F8EA3"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

export const CustomSnackbarContent = forwardRef<HTMLDivElement, CustomSnackbarContentProps>(
  ({ id, message, variant, autoHideDuration = 3000 }, ref) => {
    const { closeSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const Icon = VARIANT_ICONS[variant];
    const title = t(VARIANT_TITLE_KEYS[variant]);

    return (
      <Container
        ref={ref}
        id="notistack-snackbar"
        data-testid="app-snackbar"
        data-variant={variant}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {Icon && (
            <Box sx={{ flexShrink: 0, width: '1.5rem', height: '1.5rem' }}>
              <Icon width="24" height="24" />
            </Box>
          )}
          <Typography
            sx={{
              flex: 1,
              fontFamily: 'Neue Frutiger World',
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: '150%',
              color: '#504E5A',
            }}
          >
            {title}
          </Typography>
          <CountdownCloseButton duration={autoHideDuration} onClick={() => closeSnackbar(id)} />
        </Box>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.6,
            color: '#737085',
            wordBreak: 'break-word',
          }}
        >
          {message}
        </Typography>
      </Container>
    );
  },
);

CustomSnackbarContent.displayName = 'CustomSnackbarContent';
